import { ChevronDown } from "lucide-react";
import * as React from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Choice, OtherRow, small } from "@/features/cadence-tokens";
import {
  CALLBACK_HORIZON_DAYS, CALLBACK_PURPOSES, PURPOSE_MAX, SOON_DEFAULT, SOON_MINUTES, callbackProblem, callbackWhen,
  defaultCallback, instantOf, isoWithOffset, knownZone, outsideCallingHours, ownZone, purposeToSend, quickPicks,
  soonPick, soonProblem, untilClock, wallTime, workedUntil, zoneName, type WallTime,
} from "@/lib/callbacks";
import type { Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The day and the time of a callback (ADR-080): in the window the phone's outcome window opens for
 * one ("Schedule a callback", `softphone/acd-call.tsx`), and on Set outcome.
 *
 * Picked on the lead's clock, the one the dialer calls them on, with the rep's own beside it when
 * the two differ. It opens on the outcome's own wait — what the callback does given no time — so a
 * rep in a hurry confirms and goes; the quick picks are the times a rep says most. A few minutes
 * out is one pick, "In 10 min", whose minutes the rep changes from its arrow and which is kept as
 * theirs (`useCallbackMinutes`), so the row stays short. Outside the lead's calling hours is a
 * warning, not a refusal: the dialer waits for the hours to open. Under the time, until when the
 * cadence keeps trying with its own calls and redials, when it has a window (ACL-661).
 *
 * Under the time, what the call is for (ACL-506, ADR-162 in `platform`): a contract review, a
 * signing, a follow-up, or the rep's own words. The reminder, the pinned callback and the rep the
 * dialer may give the call to all read it.
 */

export interface CallbackTime {
  /** The clock the time is picked on. */
  zone: string;
  /** True: the lead's clock. False: nothing says where the lead is, so the rep's own. */
  leadClock: boolean;
  wall: WallTime;
  instant: number | null;
  problem: string | null;
  /** What core-api is sent, with the zone's offset; null while the time cannot be saved. */
  iso: string | null;
}

function described(zone: string, leadClock: boolean, wall: WallTime): CallbackTime {
  const instant = instantOf(wall, zone);
  const problem = callbackProblem(instant, Date.now());
  return {
    zone,
    leadClock,
    wall,
    instant,
    problem,
    iso: instant !== null && problem === null ? isoWithOffset(instant, zone) : null,
  };
}

/** The picker's state for a lead: `start` opens it on an outcome's own wait. The lead's zone can
 *  arrive after the picker opened (their card still loading): the time picked stays the same
 *  instant, said on the new clock. */
export function useCallbackTime(leadZone: string | null | undefined) {
  const lead = knownZone(leadZone);
  const zone = lead ?? ownZone();
  const [picked, setPicked] = React.useState<{ wall: WallTime; zone: string }>(() => ({
    wall: wallTime(Date.now(), zone),
    zone,
  }));
  const at = picked.zone === zone ? null : instantOf(picked.wall, picked.zone);
  const wall = at === null ? picked.wall : wallTime(at, zone);
  const set = React.useCallback((next: WallTime) => setPicked({ wall: next, zone }), [zone]);
  const start = React.useCallback(
    (outcome: Pick<Disposition, "backoff_seconds">) =>
      setPicked({ wall: wallTime(defaultCallback(outcome, Date.now()), zone), zone }),
    [zone],
  );
  // What the call is for: the words as typed, sent trimmed (`purposeToSend`).
  const [purpose, setPurpose] = React.useState("");
  return { value: described(zone, lead !== null, wall), set, start, purpose, setPurpose, purposeToSend: purposeToSend(purpose) };
}

export function CallbackPicker({
  value, onChange, disabled, minutes, onMinutes, purpose, onPurposeChange, windowMinutes,
}: {
  value: CallbackTime;
  onChange: (wall: WallTime) => void;
  disabled?: boolean;
  /** The minutes on the "In … min" pick, the rep's own (`useCallbackMinutes`); left out, the
   *  picker keeps them itself. */
  minutes?: number;
  onMinutes?: (minutes: number) => void;
  /** What the call is for, and its setter; left out, the picker asks only for the time. */
  purpose?: string;
  onPurposeChange?: (words: string) => void;
  /** How long the cadence works the callback after its time (ACL-661), from the lead's card;
   *  null or left out: just its call and redials, and nothing is said. */
  windowMinutes?: number | null;
}) {
  const id = React.useId();
  const now = Date.now();
  const [ownMinutes, setOwnMinutes] = React.useState(SOON_DEFAULT);
  const soon = minutes ?? ownMinutes;
  const soonWall = wallTime(soonPick(now, soon).instant, value.zone);
  const today = wallTime(now, value.zone).date;
  const last = wallTime(now + CALLBACK_HORIZON_DAYS * 86_400_000, value.zone).date;
  const when = value.instant !== null ? callbackWhen(value.instant, value.leadClock ? value.zone : null) : null;
  const late = value.leadClock && value.instant !== null && value.problem === null && outsideCallingHours(value.instant, value.zone);
  // Until when the cadence keeps trying for this callback, on the clock the time is said on.
  const until =
    windowMinutes !== null && windowMinutes !== undefined && value.instant !== null && value.problem === null
      ? untilClock(workedUntil(value.instant, windowMinutes), value.instant, value.leadClock ? value.zone : null)
      : null;

  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5" disabled={disabled}>
      <legend className="mb-1.5 text-xs text-text-muted">
        <span className="font-medium text-text">Call back at</span>{" "}
        {value.leadClock ? `· their time (${zoneName(value.instant ?? now, value.zone)})` : "· your time: no time zone is known for them"}
      </legend>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,7.5rem)] gap-1.5">
        <Input
          id={`${id}-date`}
          type="date"
          aria-label="Callback date"
          min={today}
          max={last}
          required
          value={value.wall.date}
          invalid={value.problem !== null && value.wall.date !== ""}
          onChange={(e) => onChange({ ...value.wall, date: e.target.value })}
        />
        <Input
          id={`${id}-time`}
          type="time"
          aria-label="Callback time"
          step={60}
          required
          value={value.wall.time}
          invalid={value.problem !== null && value.wall.time !== ""}
          onChange={(e) => onChange({ ...value.wall, time: e.target.value })}
        />
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Quick picks">
        <SoonPick
          minutes={soon}
          lit={soonWall.date === value.wall.date && soonWall.time === value.wall.time}
          onPick={() => onChange(soonWall)}
          onMinutes={(m) => {
            (onMinutes ?? setOwnMinutes)(m);
            onChange(wallTime(soonPick(Date.now(), m).instant, value.zone));
          }}
        />
        {quickPicks(now, value.zone).map((pick) => {
          const wall = wallTime(pick.instant, value.zone);
          const lit = wall.date === value.wall.date && wall.time === value.wall.time;
          return (
            <button
              key={pick.label}
              type="button"
              aria-pressed={lit}
              onClick={() => onChange(wall)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-2xs transition-colors",
                lit
                  ? "border-accent-8 bg-accent-2 text-accent-11"
                  : "border-border bg-surface text-text-muted hover:bg-surface-hover",
              )}
            >
              {pick.label}
            </button>
          );
        })}
      </div>
      {value.problem ? (
        <p role="alert" className="text-2xs text-danger-11">
          {value.problem}
        </p>
      ) : (
        when && (
          <p className="text-2xs text-text-muted" aria-live="polite">
            {when.there}
            {when.yours && <> · {when.yours}</>}
          </p>
        )
      )}
      {late && (
        <p className="text-2xs text-warning-11">
          Outside 8 AM–9 PM for them: the dialer waits until their calling hours open.
        </p>
      )}
      {until && <p className="text-2xs text-text-muted">Worked until {until}: the cadence keeps trying until then.</p>}
      {onPurposeChange && <CallbackPurpose value={purpose ?? ""} onChange={onPurposeChange} />}
    </fieldset>
  );
}

/** What the call is for: a chip for each usual purpose, and Other for the rep's own words. */
function CallbackPurpose({ value, onChange }: { value: string; onChange: (words: string) => void }) {
  const id = React.useId();
  const preset = (CALLBACK_PURPOSES as readonly string[]).includes(value);
  const [other, setOther] = React.useState(value !== "" && !preset);
  const chip = (lit: boolean) =>
    cn(
      "rounded-full border px-2 py-0.5 text-2xs transition-colors",
      lit ? "border-accent-8 bg-accent-2 text-accent-11" : "border-border bg-surface text-text-muted hover:bg-surface-hover",
    );
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <span id={`${id}-label`} className="text-xs text-text-muted">
        <span className="font-medium text-text">For</span> · what the call is about
      </span>
      <div className="flex flex-wrap gap-1" role="group" aria-labelledby={`${id}-label`}>
        {CALLBACK_PURPOSES.map((words) => (
          <button
            key={words}
            type="button"
            aria-pressed={!other && value === words}
            className={chip(!other && value === words)}
            onClick={() => {
              setOther(false);
              onChange(value === words ? "" : words);
            }}
          >
            {words}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={other}
          className={chip(other)}
          onClick={() => {
            setOther(!other);
            onChange("");
          }}
        >
          Other
        </button>
      </div>
      {other && (
        <Input
          aria-label="What the call is for"
          placeholder="e.g. Go over the medical records"
          maxLength={PURPOSE_MAX}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/** "In 10 min" and an arrow: the words set the time, the arrow chooses the minutes (which also sets
 *  it). One chip, so a callback a few minutes out costs the row one place, not one per choice. */
function SoonPick({
  minutes, lit, onPick, onMinutes,
}: {
  minutes: number;
  lit: boolean;
  onPick: () => void;
  onMinutes: (minutes: number) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const choose = (m: number) => {
    onMinutes(m);
    setOpen(false);
  };
  return (
    <span
      className={cn(
        "inline-flex items-stretch overflow-hidden rounded-full border text-2xs transition-colors",
        lit ? "border-accent-8 bg-accent-2 text-accent-11" : "border-border bg-surface text-text-muted",
      )}
    >
      <button type="button" aria-pressed={lit} onClick={onPick} className="py-0.5 pl-2 pr-1.5 hover:bg-surface-hover">
        In {minutes} min
      </button>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          setTyped("");
          setError(null);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Choose the minutes"
            className={cn(
              "border-l pl-1 pr-1.5 hover:bg-surface-hover data-[state=open]:bg-surface-hover",
              lit ? "border-accent-8" : "border-border",
            )}
          >
            <ChevronDown className="size-3" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="mb-2.5 space-y-0.5">
            <p className="text-sm font-medium text-text">Call back in</p>
            <p className="text-xs text-text-muted">Kept as yours for the next callback.</p>
          </div>
          <div className="grid grid-cols-3 gap-1.5" role="group" aria-label="Minutes">
            {SOON_MINUTES.map((m) => (
              <Choice key={m} selected={m === minutes} onClick={() => choose(m)}>
                {m} min
              </Choice>
            ))}
          </div>
          <OtherRow
            label="Other minutes"
            error={error}
            onSubmit={() => {
              const problem = soonProblem(typed);
              if (problem) return setError(problem);
              choose(Number(typed.trim()));
            }}
          >
            <input
              aria-label="Minutes"
              inputMode="numeric"
              className={small}
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
                setError(null);
              }}
            />
            <span className="text-xs text-text-muted">min</span>
          </OtherRow>
        </PopoverContent>
      </Popover>
    </span>
  );
}
